import { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query"
import {
  ACTIVITY_LOG_KEY,
  BILLING_STATUS_KEY,
  CURRENT_CHANGE_KEY,
  CURRENT_EXPERIMENT_KEY,
  CURRENT_REPORT_KEY,
  CURRENT_SESSION_REPORT_KEY,
  CURRENT_VARIANT_KEY,
  DOMAIN_SNIPPET_KEY,
  GA4_SETTINGS,
  LATEST_REPORT_SNAPSHOT_KEY,
  LIST_CHANGES_KEY,
  LIST_EXPERIMENTS_KEY,
  LIST_OBJECTIVES_KEY,
  LIST_USER_WORKSPACES_KEY,
  LIST_VARIANTS_KEY,
  LIST_WORKSPACE_DOMAINS,
  LIST_WORKSPACE_INVITES,
  LIST_WORKSPACE_MEMBERS,
  PIXEL_SNIPPET_KEY,
  SELECTED_WORKSPACE_KEY,
  SHOPIFY_SYNC_STATUS_KEY,
  USER_DETAILS_KEY,
  VARIANT_PREVIEW_LINK_KEY,
  WORKSPACE_CAPABILITIES_KEY,
} from "../constants"
import { queryKeys } from "../queryKeys"
import { Change, Experiment, Variant } from "../types"

type ExperimentListPage = {
  status: number
  experiments: Experiment[]
  pagination: {
    limit: number
    nextCursor: string | null
    hasMore: boolean
    totalCount: number
  }
}

type ExperimentListData = InfiniteData<ExperimentListPage, string | undefined>

const LIVE_DATA_QUERY_KEYS = new Set<string>([
  ACTIVITY_LOG_KEY,
  BILLING_STATUS_KEY,
  CURRENT_CHANGE_KEY,
  CURRENT_EXPERIMENT_KEY,
  CURRENT_REPORT_KEY,
  CURRENT_SESSION_REPORT_KEY,
  CURRENT_VARIANT_KEY,
  DOMAIN_SNIPPET_KEY,
  GA4_SETTINGS,
  LATEST_REPORT_SNAPSHOT_KEY,
  LIST_CHANGES_KEY,
  LIST_EXPERIMENTS_KEY,
  LIST_OBJECTIVES_KEY,
  LIST_VARIANTS_KEY,
  LIST_WORKSPACE_DOMAINS,
  LIST_WORKSPACE_INVITES,
  LIST_WORKSPACE_MEMBERS,
  PIXEL_SNIPPET_KEY,
  SELECTED_WORKSPACE_KEY,
  SHOPIFY_SYNC_STATUS_KEY,
  USER_DETAILS_KEY,
  VARIANT_PREVIEW_LINK_KEY,
  WORKSPACE_CAPABILITIES_KEY,
])

const WORKSPACE_MUTATION_QUERY_KEYS: QueryKey[] = [
  [ACTIVITY_LOG_KEY],
  [BILLING_STATUS_KEY],
  [CURRENT_REPORT_KEY],
  [CURRENT_SESSION_REPORT_KEY],
  [DOMAIN_SNIPPET_KEY],
  [GA4_SETTINGS],
  [LIST_EXPERIMENTS_KEY],
  [LIST_OBJECTIVES_KEY],
  [LIST_USER_WORKSPACES_KEY],
  [LIST_WORKSPACE_DOMAINS],
  [PIXEL_SNIPPET_KEY],
  [SELECTED_WORKSPACE_KEY],
  [SHOPIFY_SYNC_STATUS_KEY],
  [USER_DETAILS_KEY],
  [WORKSPACE_CAPABILITIES_KEY],
]

const WORKSPACE_SWITCH_PERSISTED_QUERY_KEYS = new Set<string>([
  SELECTED_WORKSPACE_KEY,
  USER_DETAILS_KEY,
])

const getQueryKeyName = (queryKey: QueryKey): string | null => {
  const firstKey = queryKey[0]
  return typeof firstKey === "string" ? firstKey : null
}

export const isWorkspaceScopedQueryKey = (queryKey: QueryKey): boolean => {
  const keyName = getQueryKeyName(queryKey)
  if (!keyName) return false
  if (WORKSPACE_SWITCH_PERSISTED_QUERY_KEYS.has(keyName)) return false
  return LIVE_DATA_QUERY_KEYS.has(keyName)
}

export const clearWorkspaceScopedQueryData = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.cancelQueries({
    predicate: (query) => isWorkspaceScopedQueryKey(query.queryKey),
  })

  queryClient.removeQueries({
    predicate: (query) => isWorkspaceScopedQueryKey(query.queryKey),
  })
}

export const invalidateWorkspaceMutationQueries = async (queryClient: QueryClient): Promise<void> => {
  await Promise.all(
    WORKSPACE_MUTATION_QUERY_KEYS.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, refetchType: "active" })
    )
  )
}

export const invalidateActivityQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.activity.all, refetchType: "active" })

const updateExperimentLists = (
  queryClient: QueryClient,
  experiment: Experiment,
): void => {
  const queries = queryClient.getQueryCache().findAll({ queryKey: queryKeys.experiments.all })

  queries.forEach((query) => {
    const listStatus = query.queryKey[2]
    queryClient.setQueryData<ExperimentListData>(query.queryKey, (current) => {
      if (!current) return current

      let removedCount = 0
      const pages = current.pages.map((page) => {
        const containsExperiment = page.experiments.some((item) => item.id === experiment.id)
        if (!containsExperiment) return page

        if (typeof listStatus === "string" && listStatus !== experiment.status) {
          removedCount += 1
          return {
            ...page,
            experiments: page.experiments.filter((item) => item.id !== experiment.id),
          }
        }

        return {
          ...page,
          experiments: page.experiments.map((item) => (
            item.id === experiment.id ? { ...item, ...experiment } : item
          )),
        }
      })

      if (removedCount === 0 || pages.length === 0) {
        return { ...current, pages }
      }

      pages[0] = {
        ...pages[0],
        pagination: {
          ...pages[0].pagination,
          totalCount: Math.max(0, pages[0].pagination.totalCount - removedCount),
        },
      }

      return { ...current, pages }
    })
  })
}

const removeExperimentFromLists = (queryClient: QueryClient, experimentId: string): void => {
  const queries = queryClient.getQueryCache().findAll({ queryKey: queryKeys.experiments.all })

  queries.forEach((query) => {
    queryClient.setQueryData<ExperimentListData>(query.queryKey, (current) => {
      if (!current) return current

      let removedCount = 0
      const pages = current.pages.map((page) => {
        const experiments = page.experiments.filter((experiment) => experiment.id !== experimentId)
        removedCount += page.experiments.length - experiments.length
        return experiments.length === page.experiments.length ? page : { ...page, experiments }
      })

      if (removedCount > 0 && pages.length > 0) {
        pages[0] = {
          ...pages[0],
          pagination: {
            ...pages[0].pagination,
            totalCount: Math.max(0, pages[0].pagination.totalCount - removedCount),
          },
        }
      }

      return { ...current, pages }
    })
  })
}

export const patchExperimentCache = (
  queryClient: QueryClient,
  experiment: Experiment,
): void => {
  queryClient.setQueryData<Experiment>(
    queryKeys.experiments.detail(experiment.id),
    (current) => current ? { ...current, ...experiment } : experiment,
  )
  updateExperimentLists(queryClient, experiment)
}

export const syncExperimentCache = async (
  queryClient: QueryClient,
  experiment: Experiment,
): Promise<void> => {
  patchExperimentCache(queryClient, experiment)

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all, refetchType: "active" }),
    invalidateActivityQueries(queryClient),
  ])
}

export const registerCreatedExperiment = async (
  queryClient: QueryClient,
  experiment: Experiment,
): Promise<void> => {
  queryClient.setQueryData(queryKeys.experiments.detail(experiment.id), experiment)
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all, refetchType: "active" }),
    invalidateActivityQueries(queryClient),
  ])
}

export const removeExperimentCache = async (
  queryClient: QueryClient,
  experimentId: string,
): Promise<void> => {
  await queryClient.cancelQueries({ queryKey: queryKeys.experiments.detail(experimentId), exact: true })

  const variantIds = new Set<string>()
  const variants = queryClient.getQueryData<Variant[]>(queryKeys.variants.list(experimentId)) ?? []
  variants.forEach((variant) => variantIds.add(variant.id))
  queryClient.getQueryCache().findAll({ queryKey: [CURRENT_VARIANT_KEY] }).forEach((query) => {
    const variant = query.state.data as Variant | undefined
    if (variant?.experiment_id === experimentId) {
      variantIds.add(variant.id)
    }
  })

  removeExperimentFromLists(queryClient, experimentId)
  queryClient.removeQueries({ queryKey: queryKeys.experiments.detail(experimentId), exact: true })
  queryClient.removeQueries({ queryKey: queryKeys.experiments.reports(experimentId) })
  queryClient.removeQueries({ queryKey: queryKeys.experiments.sessionReports(experimentId) })
  queryClient.removeQueries({ queryKey: queryKeys.experiments.snapshotMetadata(experimentId), exact: true })
  queryClient.removeQueries({ queryKey: queryKeys.variants.list(experimentId), exact: true })

  variantIds.forEach((variantId) => {
    queryClient.removeQueries({ queryKey: queryKeys.variants.detail(variantId), exact: true })
    queryClient.removeQueries({ queryKey: queryKeys.changes.list(variantId), exact: true })
  })
  queryClient.removeQueries({
    predicate: (query) => {
      if (query.queryKey[0] !== CURRENT_CHANGE_KEY) return false
      const change = query.state.data as Change | undefined
      return Boolean(change && variantIds.has(change.variant_id))
    },
  })

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all, refetchType: "active" }),
    invalidateActivityQueries(queryClient),
  ])
}

export const syncVariantCache = async (
  queryClient: QueryClient,
  variant: Variant,
): Promise<void> => {
  const normalizedVariant = { ...variant, weight: Number(variant.weight || 0) }
  queryClient.setQueryData<Variant>(
    queryKeys.variants.detail(variant.id),
    (current) => current ? { ...current, ...normalizedVariant } : normalizedVariant,
  )
  queryClient.setQueryData<Variant[]>(queryKeys.variants.list(variant.experiment_id), (current) => {
    if (!current) return current
    const exists = current.some((item) => item.id === variant.id)
    return exists
      ? current.map((item) => item.id === variant.id ? { ...item, ...normalizedVariant } : item)
      : [...current, { ...normalizedVariant, _count: normalizedVariant._count ?? { changes: 0 } }]
  })

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.variants.list(variant.experiment_id), exact: true, refetchType: "active" }),
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(variant.experiment_id), exact: true, refetchType: "active" }),
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all, refetchType: "active" }),
    invalidateActivityQueries(queryClient),
  ])
}

export const syncVariantsCache = async (
  queryClient: QueryClient,
  experimentId: string,
  variants: Variant[],
): Promise<void> => {
  const currentVariants = queryClient.getQueryData<Variant[]>(queryKeys.variants.list(experimentId)) ?? []
  const currentById = new Map(currentVariants.map((variant) => [variant.id, variant]))
  const normalizedVariants = variants.map((variant) => ({
    ...currentById.get(variant.id),
    ...variant,
    weight: Number(variant.weight || 0),
  }))
  queryClient.setQueryData(queryKeys.variants.list(experimentId), normalizedVariants)
  normalizedVariants.forEach((variant) => {
    queryClient.setQueryData<Variant>(
      queryKeys.variants.detail(variant.id),
      (current) => current ? { ...current, ...variant } : variant,
    )
  })

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.variants.list(experimentId), exact: true, refetchType: "active" }),
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(experimentId), exact: true, refetchType: "active" }),
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all, refetchType: "active" }),
    invalidateActivityQueries(queryClient),
  ])
}

export const removeVariantCache = async (
  queryClient: QueryClient,
  experimentId: string,
  variantId: string,
): Promise<void> => {
  await queryClient.cancelQueries({ queryKey: queryKeys.variants.detail(variantId), exact: true })
  queryClient.setQueryData<Variant[]>(queryKeys.variants.list(experimentId), (current) =>
    current?.filter((variant) => variant.id !== variantId),
  )
  queryClient.removeQueries({ queryKey: queryKeys.variants.detail(variantId), exact: true })
  queryClient.removeQueries({ queryKey: queryKeys.changes.list(variantId), exact: true })
  queryClient.removeQueries({
    predicate: (query) => {
      if (query.queryKey[0] !== CURRENT_CHANGE_KEY) return false
      return (query.state.data as Change | undefined)?.variant_id === variantId
    },
  })

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.variants.list(experimentId), exact: true, refetchType: "active" }),
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(experimentId), exact: true, refetchType: "active" }),
    queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all, refetchType: "active" }),
    invalidateActivityQueries(queryClient),
  ])
}

export const invalidateChangeMutationQueries = (
  queryClient: QueryClient,
  experimentId: string,
  variantId: string,
) => Promise.all([
  queryClient.invalidateQueries({ queryKey: queryKeys.changes.list(variantId), exact: true, refetchType: "active" }),
  queryClient.invalidateQueries({ queryKey: queryKeys.variants.detail(variantId), exact: true, refetchType: "active" }),
  queryClient.invalidateQueries({ queryKey: queryKeys.variants.list(experimentId), exact: true, refetchType: "active" }),
  queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(experimentId), exact: true, refetchType: "active" }),
  queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all, refetchType: "active" }),
  invalidateActivityQueries(queryClient),
])

export const syncChangeCache = async (
  queryClient: QueryClient,
  experimentId: string,
  change: Change,
): Promise<void> => {
  queryClient.setQueryData<Change>(
    queryKeys.changes.detail(change.id),
    (current) => current ? { ...current, ...change } : change,
  )
  queryClient.setQueryData<Change[]>(queryKeys.changes.list(change.variant_id), (current) => {
    if (!current) return current
    const exists = current.some((item) => item.id === change.id)
    return exists
      ? current.map((item) => item.id === change.id ? { ...item, ...change } : item)
      : [change, ...current]
  })

  await invalidateChangeMutationQueries(queryClient, experimentId, change.variant_id)
}

export const removeChangeCache = async (
  queryClient: QueryClient,
  experimentId: string,
  variantId: string,
  changeId: string,
): Promise<void> => {
  await queryClient.cancelQueries({ queryKey: queryKeys.changes.detail(changeId), exact: true })
  queryClient.setQueryData<Change[]>(queryKeys.changes.list(variantId), (current) =>
    current?.filter((change) => change.id !== changeId),
  )
  queryClient.removeQueries({ queryKey: queryKeys.changes.detail(changeId), exact: true })

  await invalidateChangeMutationQueries(queryClient, experimentId, variantId)
}
